/* eslint max-lines: "off" */

import _ from 'underscore'
import React, { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import Card from 'components/Card'
import CustomersCard from 'pages/shared/NotificationRecipientForm/CustomersCard'
import CheckEmailModal from 'pages/shared/NotificationRecipientForm/CheckEmailModal'
import Form from 'pages/shared/Form'
import MutedNotificationsCard from 'pages/shared/NotificationRecipientForm/MutedNotificationsCard'
import NotFound from 'pages/NotFound'
import Spinner from 'components/Spinner'
import TextInput from 'components/TextInput'

import AlertEmailSubscriptionCard from 'pages/shared/NotificationRecipientForm/AlertEmailSubscriptionCard'

import Api from 'services/api'

import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'
import { isEmailValid } from 'helpers/validations'
import { scrubText } from 'helpers/inputCleaners'

import { useSessionContext } from 'hooks/SessionContext'
import useCustomersPiiInfo from 'hooks/useCustomersPiiInfo'
import useNotificationRecipient from 'hooks/useNotificationRecipient'

import { PageHeader, PageTitle, screenSize, SinglePanePage } from 'styles/shared'
import { color } from 'styles/colors'

import { NotificationRecipient } from 'types/NotificationRecipient'

import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type Props = {
  title: string
  readOnlyMessage: string
  onSaveSucceeded: (response: NotificationRecipient | null) => NotificationRecipient | void
  onDelete: () => void
  onValidatedSave: (recipient: NotificationRecipient) => Promise<void>
  displayDeleteButton: boolean
  displayMutedNotifications: boolean
  customerCardCollapsed: boolean
}

const NotificationRecipientForm = ({
  title,
  readOnlyMessage,
  onValidatedSave,
  displayDeleteButton = false,
  displayMutedNotifications = false,
  onSaveSucceeded = () => {},
  onDelete = () => {},
  customerCardCollapsed = false
}: Props) => {
  const navigate = useNavigate()

  const { session: { readOnly, dealerId } } = useSessionContext()
  const { customers, areCustomersLoaded } = useCustomersPiiInfo()

  const { notificationRecipientId = '' } = useParams()

  const { notificationRecipient = {}, isNotificationRecipientLoaded } = useNotificationRecipient(dealerId, parseInt(notificationRecipientId))

  const [isSaving, setIsSaving] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showCheckEmailModal, setShowCheckEmailModal] = useState(false)

  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()
  const componentType = 'Notification Recipient'

  const fieldValue = useCallback((fieldName: string, defaultValue: string | boolean | [] = '') => {
    if (typeof defaultValue === 'boolean') {
      return (notificationRecipient && fieldName in notificationRecipient && notificationRecipient[fieldName]) || defaultValue
    } else {
      return notificationRecipient?.[fieldName] || defaultValue
    }
  }, [notificationRecipient])

  const [recipient, setRecipient] = useState({
    name: fieldValue('name'),
    email: fieldValue('email'),
    id: fieldValue('id'),
    dealerId: fieldValue('dealerId'),
    customerIds: fieldValue('customerIds', []),
    majorAlerts: fieldValue('majorAlerts', true),
    criticalAlerts: fieldValue('criticalAlerts', true),
    majorSdaAlerts: fieldValue('majorSdaAlerts', true),
    criticalSdaAlerts: fieldValue('criticalSdaAlerts', true),
    companyUpdateAlerts: fieldValue('companyUpdateAlerts', true),
    dailyDigestCriticalAlerts: fieldValue('dailyDigestCriticalAlerts', true),
    dailyDigestMajorAlerts: fieldValue('dailyDigestMajorAlerts', false),
    dailyDigestCriticalSdaAlerts: fieldValue('dailyDigestCriticalSdaAlerts', false),
    dailyDigestMajorSdaAlerts: fieldValue('dailyDigestMajorSdaAlerts', false),
    mutedAlertCodes: fieldValue('mutedAlertCodes', []),
    mutedCustomerIds: fieldValue('mutedCustomerIds', []),
    mutedDeviceIdsForAlertCodes: fieldValue('mutedDeviceIdsForAlertCodes', []),
    verifyEmail: false
  })
  const [initialAttributes, setInitialAttributes] = useState(recipient)
  const [errors, setErrors] = useState< { [key: string]: string } >({})

  useEffect(() => {
    if (isNotificationRecipientLoaded) {
      setRecipient({ ...recipient, ...notificationRecipient })
      setInitialAttributes({ ...recipient, ...notificationRecipient })
    }
  }, [isNotificationRecipientLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecipientSave = useCallback((recipientValues, updateState) => {
    const trimmedAttributes = _.mapObject(recipientValues, value => typeof value === 'string' ? value.trim() : value) // TODO: make this more generic for all forms
    const newErrors = validateNotificationRecipients({
      name: trimmedAttributes.name,
      email: trimmedAttributes.email
    })

    if (_.isEmpty(newErrors)) {
      onValidatedSave(trimmedAttributes)
        .then((response) => {
          Apptentive.sendEvent('notification_recipient_added')
          if (recipientValues.verifyEmail) {
            setShowCheckEmailModal(true)
          } else {
            navigate('/notifications')
          }
          if (response !== undefined) onSaveSucceeded(response)
          updateState(false)
        })
        .catch((e) => {
          if (typeof e.json === 'function') {
            e.json().then(errorResponse => readServerError(errorResponse))
          }

          updateState(false)
        })

      GA4.sendEvent({ category: `${title} Page`, action: 'Click Save - Succeeded' })
    } else {
      updateState(false)
      setErrors(newErrors)
    }
  }, [onValidatedSave, title, onSaveSucceeded, navigate])

  const onClickSave = useCallback(() => {
    if (readOnly) {
      alert({ title: 'Read-only', description: readOnlyMessage })
      return
    }
    setIsSaving(true)
    handleRecipientSave(recipient, setIsSaving)
  }, [recipient, readOnly, readOnlyMessage, handleRecipientSave, alert])

  const onResendVerificationLink = useCallback(() => {
    setIsResending(true)
    recipient.verifyEmail = true
    handleRecipientSave(recipient, setIsResending)
  }, [recipient, handleRecipientSave])

  const closeCallback = () => {
    setShowCheckEmailModal(false)
    navigate('/notifications')
  }

  const onClickCancel = useCallback(async () => {
    const hasChanges = !_.isMatch(recipient, initialAttributes)

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
    })

    const confirmed = !hasChanges || (hasChanges && isConfirmed)

    if (confirmed) {
      navigate('/notifications')
      GA4.sendEvent({ category: `${title} Page`, action: 'Click Cancel - Changes Discarded' })
    }
  }, [recipient, initialAttributes, navigate, title, confirm])

  const onClickDelete = useCallback(async () => {
    if (readOnly) {
      alert({
        title: 'Read-only',
        description: "Read-only view: can't delete notification recipient"
      })

      return
    }

    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
      description: CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
    })

    if (isConfirmed) {
      setIsDeleting(true)
      Api.NotificationRecipients.delete(recipient.dealerId, recipient.id)
        .then(() => {
          navigate('/notifications')
          onDelete()
        })
        .finally(() => {
          setIsDeleting(false)
        })

      GA4.sendEvent({ category: `${title} Page`, action: 'Click Delete - Confirmed' })
    }
  }, [readOnly, title, onDelete, recipient, navigate, alert, confirm])

  const validateNotificationRecipients = ({ name, email }) => {
    const errors = {}
    if (!name) errors['name'] = 'name is required'
    if (!email) errors['email'] = 'email is required'

    if (email && !isEmailValid(email)) errors['email'] = 'Invalid email'

    return errors
  }

  const readServerError = (errorResponse) => {
    const errors = {}

    Object.keys(errorResponse).forEach(key => {
      errors[key] = errorResponse[key][0].name
    })

    setErrors(errors)
  }

  if (!areCustomersLoaded || !isNotificationRecipientLoaded) {
    return <Spinner variant="page" />
  }

  if (notificationRecipientId && _.isEmpty(notificationRecipient)) {
    return <NotFound />
  }
  const showResendVerificationButton = notificationRecipient?.isVerificationPending || notificationRecipient?.isVerificationTokenExpired

  return (
    <>
      <S.Container>
        <S.PageHeader>
          <S.PageTitle>{title}</S.PageTitle>
        </S.PageHeader>
        <SinglePanePage>
          <S.Form
          {...{ readOnly, displayDeleteButton, isSaving, isDeleting }}
          onSaveCallback={onClickSave}
          onCancelCallback={onClickCancel}
          onDeleteCallback={onClickDelete}
          deleteButtonText="Recipient"
          >
            <S.Card>
              <S.TextInput
              autoFocus
              label="Name"
              onChange={event => setRecipient({ ...recipient, ...{ name: scrubText(event.target.value) } })}
              errorMessage={errors.name}
              value={recipient.name}
              isRequired
              />
              <S.FormGroup>
                <S.FormField>
                  <S.EmailInput
                  label="Email"
                  onChange={event => setRecipient({ ...recipient, ...{ email: scrubText(event.target.value) }, ...{ verifyEmail: initialAttributes.email !== scrubText(event.target.value).trim() } })}
                  errorMessage={errors.email}
                  value={recipient.email}
                  isRequired
                  />
                  {showResendVerificationButton && <span> Must verify email address within 24 hours before alert emails will be sent.</span>}
                </S.FormField>
                <div>
                  {notificationRecipient?.isVerificationPending && !notificationRecipient?.isVerificationTokenExpired && <S.StatusText>Pending verification</S.StatusText>}
                  {notificationRecipient?.isVerificationTokenExpired && <S.StatusText> Expired verification</S.StatusText>}
                  {showResendVerificationButton && <Button decoration="positive" enabled={!isResending} text="Resend Verification Link" styles={verifyButtonStyle} onPress={onResendVerificationLink} showSpinner={isResending} />}
                </div>
              </S.FormGroup>
            </S.Card>

            <S.AlertEmailSubscriptionCard collapsedByDefault={false} alerts={recipient} setAlerts={(alerts) => setRecipient({ ...recipient, ...alerts })} />
            {customers && customers?.length > 0 && <S.CustomersCard collapsedByDefault={customerCardCollapsed} customers={customers} recipient={recipient} onChangeCustomerIds={(newCustomerIds) => setRecipient({ ...recipient, ...{ customerIds: newCustomerIds } })} />}
            {displayMutedNotifications && (
            <>
              { /* must use notificationRecipient (not recipient) for muted values to avoid a race condition */}
              <S.MutedNotificationsCard
                mutedAlertCodes={notificationRecipient?.mutedAlertCodes || []}
                mutedCustomerIds={notificationRecipient?.mutedCustomerIds || []}
                mutedDeviceIdsForAlertCodes={notificationRecipient?.mutedDeviceIdsForAlertCodes || []}
                onChange={({ mutedAlertCodes, mutedCustomerIds, mutedDeviceIdsForAlertCodes }) => setRecipient({ ...recipient, ...{ mutedAlertCodes, mutedCustomerIds, mutedDeviceIdsForAlertCodes } })}
                customers={customers || []}
              />
            </>
            )}
          </S.Form>
        </SinglePanePage>
      </S.Container>
      <CheckEmailModal open={showCheckEmailModal} closeCallback={closeCallback} />
    </>
  )
}

const cardStyle = css`
  width: 100%;
  margin-bottom: 10px;
`
const verifyButtonStyle: ButtonStyles = {
  button: {
    height: 'auto',
    minHeight: 34,
    maxWidth: 210
  },
  text: {
    textAlign: 'center'
  }
}

const S = {
  Container: styled.div`
    width: 100%;
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      padding-top: 10px;
    }
  `,
  PageTitle: styled(PageTitle)`
    @media ${screenSize.small} {
      display: block;
    }
  `,
  Form: styled(Form)`
    max-width: unset;

    > div {
      border: none;
      padding: 0 10px;
      margin-top: 10px;
      margin-bottom: 60px;
      height: unset;
    }
  `,
  Card: styled(Card)`${cardStyle}`,
  AlertEmailSubscriptionCard: styled(AlertEmailSubscriptionCard)`${cardStyle}`,
  CustomersCard: styled(CustomersCard)`${cardStyle}`,
  MutedNotificationsCard: styled(MutedNotificationsCard)`${cardStyle}`,
  TextInput: styled(TextInput)`
    display: block;
    width: 100%;
    max-width: 800px;
  `,
  FormGroup: styled.div`
    max-width: 960px;
    display: grid;
    grid-template-columns: auto auto;
    grid-column-gap: 10px;
  `,
  FormField: styled.div`
    width: 100%;
  `,
  EmailInput: styled(TextInput)`
    width: 100%;
    margin-bottom: 0px;
    max-width: 800px;
  `,
  StatusText: styled.span`
    text-transform: uppercase;
    font-size: 12px;
    font-style: italic;
    color: ${color('errorMessage')};
  `
}

export default NotificationRecipientForm
